import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { Github, Mail, Lock, ArrowRight, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";
import { ALLOWED_EMAILS } from "@/utils/config";
import { useTheme } from "@/contexts/ThemeContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [isGithubLoading, setIsGithubLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isEmailVerifying, setIsEmailVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const { loginWithGoogle, loginWithGitHub } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { theme } = useTheme();
  
  useEffect(() => {
    const location = window.location.pathname;
    if (location == "/auth/login" && localStorage.getItem("token")) {
      window.location.pathname = "/dashboard";
    }
    
    // Hide navbar specifically for auth pages
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  const handleGoogleLogin = async () => {
    try {
      setIsGoogleLoading(true);
      await loginWithGoogle();
    } catch (error) {
      console.error("Google login failed:", error);
      toast({
        variant: "destructive",
        title: "Authentication Failed",
        description: "Failed to initialize Google login. Please try again.",
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGithubLogin = async () => {
    try {
      setIsGithubLoading(true);
      await loginWithGitHub();
    } catch (error) {
      console.error("GitHub login failed:", error);
      toast({
        variant: "destructive",
        title: "Authentication Failed",
        description: "Failed to initialize GitHub login. Please try again.",
      });
    } finally {
      setIsGithubLoading(false);
    }
  };

  return (
    <div className={`fixed inset-0 w-full h-full flex items-center justify-center ${theme === 'dark' ? 'bg-[#030405]' : 'bg-background'} z-50 overflow-hidden`}>
      {/* Background grid pattern */}
      {theme === 'dark' && (
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f25_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f25_1px,transparent_1px)] bg-[size:15px_15px] opacity-40"></div>
      )}
      
      {/* Floating particles - reduced number for cleaner look */}
      {Array(4)
        .fill(null)
        .map((_, i) => (
          <motion.div
            key={i}
            className={`absolute rounded-full ${theme === 'dark' 
              ? 'bg-emerald-600/5' 
              : 'bg-emerald-400/5'} blur-xl`}
            style={{
              width: Math.random() * 80 + 40,
              height: Math.random() * 80 + 40,
            }}
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0,
            }}
            animate={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{
              duration: Math.random() * 15 + 10,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}

      {/* Gradient orbs */}
      <motion.div
        className={`absolute bottom-1/3 -right-40 w-96 h-96 rounded-full blur-3xl ${theme === 'dark' 
          ? 'bg-gradient-to-r from-primary/20 to-primary/5' 
          : 'bg-gradient-to-r from-emerald-500/20 to-emerald-400/15'}`}
        animate={{ x: [0, 60, 0], y: [0, -40, 0], scale: [1, 1.2, 1], opacity: [0.5, 0.7, 0.5] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />
      
      <motion.div
        className={`absolute -top-20 left-1/3 w-72 h-72 rounded-full blur-3xl ${theme === 'dark' 
          ? 'bg-gradient-to-r from-primary/10 to-primary/5' 
          : 'bg-gradient-to-r from-emerald-600/15 to-emerald-400/10'}`}
        animate={{ x: [-20, 40, -20], y: [10, 40, 10], scale: [1.1, 0.9, 1.1], opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Login Form Container */}
      <motion.div
        className={`w-full max-w-md p-8 sm:p-10 rounded-xl shadow-lg relative z-10 m-6 ${theme === 'dark'
          ? 'bg-[#050607]/70 border border-gray-800/50 backdrop-blur-sm' 
          : 'bg-white border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]'}`}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="text-center" variants={itemVariants}>
          {/* Logo */}
          <div className="mb-6">
            <img
              src="/docgen-logo.png"
              alt="DocGen Logo"
              className="h-12 w-12 mx-auto"
            />
          </div>

          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
            Welcome back
          </h2>
          <p className={`mt-2 mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Sign in to your DocGen account
          </p>
          
          <div className="space-y-4 mb-6">
            <Button
              onClick={handleGoogleLogin}
              className={`w-full h-12 transition-all duration-300 shadow-sm rounded-lg group relative overflow-hidden ${theme === 'dark'
                ? 'bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white border border-emerald-600/50'
                : 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white border border-emerald-400/20'}`}
              disabled={isGoogleLoading}
            >
              {isGoogleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <FcGoogle className="w-5 h-5 mr-2" />
              )}
              Sign in with Google
            </Button>

            <Button
              onClick={handleGithubLogin}
              className={`w-full h-12 transition-all duration-300 shadow-sm rounded-lg group relative overflow-hidden ${theme === 'dark'
                ? 'bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-700 hover:to-gray-600 text-white border border-gray-700/50' 
                : 'bg-gradient-to-r from-gray-700 to-gray-600 hover:from-gray-600 hover:to-gray-500 text-white border border-gray-500/20'}`}
              disabled={isGithubLoading}
            >
              {isGithubLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Github className="w-5 h-5 mr-2" />
              )}
              Sign in with GitHub
            </Button>
          </div>

          <motion.div className="text-sm" variants={itemVariants}>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              By continuing, you agree to our{" "}
              <Link to="/terms-of-use" className={`underline ${theme === 'dark' ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'}`}>
                Terms of Service
              </Link>
              {" and "}
              <Link to="/privacy-policy" className={`underline ${theme === 'dark' ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'}`}>
                Privacy Policy
              </Link>
              .
            </p>
          </motion.div>

          <motion.div className="mt-4 text-center" variants={itemVariants}>
            <Link to="/" className={`text-sm ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'} transition-colors`}>
              ← Back to Home
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}
